import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { faTimes, faUndo } from '@fortawesome/pro-regular-svg-icons';
import { useParams } from 'react-router-dom';

import toast from 'src/common/toast';
import { Button } from 'src/common/components/buttons/Button';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';
import { Tooltip } from 'src/common/components/Tooltip';
import { TechCrew } from 'src/project/types/techCrews';
import { updateTechCrewServiceSpecificationPath } from 'src/project/api/techCrewsApi';
import {
  NewServiceSpecification,
  ServiceSpecificationType,
} from 'src/project/types/projects';
import { ServiceSpecificationList } from './ServiceSpecificationList';
import { ServiceSpecificationTreeOnlv } from './ServiceSpecificationTreeOnlv';
import { ServiceSpecificationTreeGaeb } from './ServiceSpecificationTreeGaeb';

export const SelectRegieServiceSpecification = ({
  techCrew,
  serviceSpecification,
  onSave,
}: {
  techCrew: TechCrew;
  serviceSpecification: NewServiceSpecification;
  onSave: () => void;
}) => {
  const { t } = useTranslation(['project', 'common']);
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(
    techCrew.serviceSpecificationPathsRegie?.paths || [],
  );
  const [expanded, setExpanded] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSavePaths = async () => {
    setIsSaving(true);
    const data = {
      serviceSpecificationPathsRegie: {
        serviceSpecification: serviceSpecification._id,
        paths: checked,
      },
    };
    await updateTechCrewServiceSpecificationPath(
      projectId,
      techCrew._id,
      data,
      'REGIE',
    );
    await queryClient.invalidateQueries({
      queryKey: ['projects', projectId, 'techCrews'],
    });
    setIsSaving(false);
    toast.success(t('techCrewEdit.selectRegieBoq.reloadPositions'));
    onSave();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <div className="flex items-baseline">
          <Tooltip
            content={t('techCrewEdit.selectRegieBoq.selectSavedPositions')}
          >
            <div>
              <ActionIcon
                onClick={() =>
                  setChecked(
                    techCrew.serviceSpecificationPathsRegie?.paths || [],
                  )
                }
              >
                <FontAwesomeIcon icon={faUndo} />
              </ActionIcon>
            </div>
          </Tooltip>
          <span className="mr-1 text-sm text-gray-600">
            {t('techCrewEdit.selectRegieBoq.savedPositions')}
            {': '}
          </span>
          <span className="mr-3 text-sm font-medium text-shuttleGray-700">
            {techCrew?.serviceSpecificationPathsRegie?.paths?.length || 0}
          </span>{' '}
          <Tooltip
            content={t('techCrewEdit.selectRegieBoq.removeCurrentSelection')}
          >
            <div>
              <ActionIcon className="mr-1" onClick={() => setChecked([])}>
                <FontAwesomeIcon icon={faTimes} />
              </ActionIcon>
            </div>
          </Tooltip>
          <span className="mr-1 text-sm text-gray-600">
            {t('techCrewEdit.selectRegieBoq.selectedPositions')}
            {': '}
          </span>
          <span className="w-12 text-sm font-medium text-shuttleGray-700">
            {checked.length || 0}
          </span>
        </div>
        <Button onClick={handleSavePaths} className="w-32" loading={isSaving}>
          {t('common:button.save')}
        </Button>
      </div>

      {serviceSpecification.specificationType ===
      ServiceSpecificationType.ONLV ? (
        <ServiceSpecificationTreeOnlv
          serviceSpecification={serviceSpecification}
          ssLevel="LV"
          checked={checked}
          onCheck={setChecked}
          expanded={expanded}
          onExpand={setExpanded}
        />
      ) : serviceSpecification.specificationType ===
        ServiceSpecificationType.GAEB ? (
        <ServiceSpecificationTreeGaeb
          serviceSpecification={serviceSpecification}
          checked={checked}
          onCheck={setChecked}
          expanded={expanded}
          onExpand={setExpanded}
        />
      ) : (
        <ServiceSpecificationList
          serviceSpecification={serviceSpecification}
          checked={checked}
          onCheck={setChecked}
        />
      )}
    </div>
  );
};
